
import { Component, InjectReactive, Ref, Vue, Watch, Inject, Prop } from 'vue-property-decorator';
import { Route } from 'vue-router';
import { namespace } from 'vuex-class';

import AdUnit from '@/components/AdUnit.vue';
import LazyClientShow from '@/components/LazyClientShow.vue';
import LoginButton from '@/components/LoginButton.vue';
import QuickActionBar from '@/components/QuickActionBar.vue';
import SimilarTitlesGraphql from '@/components/SimilarTitlesGraphql.vue';
import BuyBox /* webpackChunkName: "buybox" */ from '@/components/buybox/BuyBox.vue';
import PromotedBuyBox /* webpackChunkName: "promoted-buybox" */ from '@/components/buybox/PromotedBuyBox.vue';
import InfoBox from '@/components/details/InfoBox.vue';
import InfoBoxWrapper from '@/components/details/InfoBoxWrapper.vue';
import TitleBackdrops from '@/components/details/TitleBackdrops.vue';
import TitleBackdropsWrapper from '@/components/details/TitleBackdropsWrapper.vue';
import TitleBlock from '@/components/details/TitleBlock.vue';
import TitleInfo from '@/components/details/TitleInfo.vue';
import ArticleBlock from '@/components/details/editorial/ArticleBlock.vue';
import ProductionNewsBlock from '@/components/details/editorial/ProductionNewsBlock.vue';
import GuideList from '@/components/editorial/GuideList.vue';
import { AvailabilityBadgeType } from '@/components/poster/types/availability-badge';

import SimilarTitlesProvider from '@/helpers/providers/similar-titles-provider';

import { diffInDays, diffInMilliseconds } from '@/helpers/date-helper';
import { showFreemium } from '@/helpers/iap-helper';

import { toastError } from '@/helpers/toast/toast-helper';
import { TrackingAppId, TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { TrafficHelper } from '@/helpers/traffic-helper';

import { QuickActionBarProps } from '@/interfaces/components/QuickActionBar';
import { TitleListName } from '@/interfaces/responses/title-list';
import { TitleContentType } from '@/interfaces/snowplow/title-context';
import { useArbitrage } from '@/helpers/composables/useArbitrage';
import { UseQueryReturn, useQuery } from '@/helpers/composables/use-query/useQuery';
import { supportedObjectTypes, supportedFormats } from '@/components/sponsored-recommendations/useSponsoredRec';

import {
	CreditRole,
	MonetizationType,
	ObjectType,
	Platform,
	SraPlacement,
	TitleFilter,
	TitleListType,
} from '@/@types/graphql-types';

import { UserSettings } from '@/stores/user.store';

import { PopularTitle } from '@/interfaces/titles';
import {
	GetTitleDetailArticleDocument,
	GetTitleDetailArticleQuery,
	GetTitleDetailArticleQueryVariables,
	TitleDetailArticleFragment,
} from '@/pages/graphql/queries/GetTitleDetailArticle.query';
import {
	GetTitleDetailSnippetsDocument,
	GetTitleDetailSnippetsQuery,
	GetTitleDetailSnippetsQueryVariables,
	TitleDetailSnippetFragment,
} from '@/pages/graphql/queries/GetTitleDetailSnippets.query';
import {
	GetUrlTitleDetailsDocument,
	GetUrlTitleDetailsQuery,
	GetUrlTitleDetailsQueryVariables,
} from '@/pages/graphql/queries/GetUrlTitleDetails.query';
import {
	GetNodeTitleDetailsDocument,
	GetNodeTitleDetailsQuery,
	GetNodeTitleDetailsQueryVariables,
} from '@/pages/graphql/queries/GetNodeTitleDetails.query';

import type { AssertByTypename } from '@/helpers/graphql-model-helper';
import { getBackdropClips } from '@/helpers/title-helper';
import { ModalHelper } from '@/helpers/modal-helper';
import type {
	SnowplowContext,
	SnowplowModuleContext,
	SnowplowTitleContextGraphqlPayload,
} from '@/helpers/tracking/providers/snowplow-contexts';
import type { Content } from '@/interfaces/components/content';
import type { SponsoredAdFragment } from '@/pages/graphql/fragments/SponsoredAd.fragment';

import { streamingChartCountries } from '@/components/details/streaming-chart/streaming-chart-config';

import type { Genres } from '@/constants/types';

import { GenresByShortNameConstant } from '@/constants/ConstantsProvider.vue';
// @ts-ignore package does not have a module declaration
import { hydrateWhenVisible } from 'vue-lazy-hydration';
import { WebLocale } from '@/enums/web-locale';

import SponsoredRecommendation from '@/components/sponsored-recommendations/SponsoredRecommendation.vue';
import { disableScroll, enableScroll } from '@/helpers/scroll-helper';

const AvailabilityBadge = () => import('@/components/poster/AvailabilityBadge.vue');
const EpisodesList = () => import('@/components/EpisodesList.vue');
const HorizontalTitleListGraphql = () => import('@/components/timeline/HorizontalTitleListGraphql.vue');
const PosterGraphql = () => import('@/components/poster/PosterGraphql.vue');
const SeasonsList = () => import('@/components/SeasonsList.vue');
const TitleClips = () => import('@/components/details/TitleClips.vue');
const TitleCredits = () => import('@/components/details/TitleCredits.vue');
const TitleListProvider = () => import('@/helpers/providers/title-list-provider');
const ProvideStreamingChartData = () => import('@/components/streaming-charts/ProvideStreamingChartData.vue');
const TitleSynopsis = () => import('@/components/details/TitleSynopsis.vue');
const TitleDetailStreamingChart = () => import('@/components/details/streaming-chart/TitleDetailStreamingChart.vue');
const SpinningText = () => import('@/components/details/SpinningText.vue');

const LazyWhyToWatch = hydrateWhenVisible(() => import('@/components/details/WhyToWatch.vue'));
const LazyUpcomingContent = hydrateWhenVisible(() => import('@/components/upcoming/UpcomingContent.vue'));
const PopularTitlesProvider = () => import('@/helpers/providers/popular-titles-provider');
const PlexPlayerModal = () => import('@/ui-components/PlexPlayerModal.vue');

// import BuyBoxNew from '@/components/buybox-new/BuyBoxNew.vue';

const App = namespace('app');
const Experiment = namespace('experiment');
const Language = namespace('language');
const Routing = namespace('routing');
const TitleDetailsStore = namespace('titleDetails');
const User = namespace('user');

// APPLE_BACKDROP_EXP
import { AppleBackdropExpName } from '@/components/experiments/AppleBackdropExp';
import { validateAppleBackdropExperiment } from '@/components/experiments/AppleBackdropExp/helpers';
// APPLE_BACKDROP_EXP

// UPDATE_META_DESCRIPTIONS
import { allowedCountryCodes, appendMetaDescription } from '@/components/experiments/TitleMetaDescriptions/utils';
import { titleMetaDescriptionsData } from '@/components/experiments/TitleMetaDescriptions/data';
import { timestamp } from '@/helpers/date-helper';
// UPDATE_META_DESCRIPTIONS

@Component({
	name: 'TitleDetail',
	components: {
		AdUnit,
		ArticleBlock,
		AvailabilityBadge,
		BuyBox,
		// BuyBoxNew,
		EpisodesList,
		GuideList,
		HorizontalTitleListGraphql,
		InfoBoxWrapper,
		InfoBox,
		LazyClientShow,
		LoginButton,
		PosterGraphql,
		ProductionNewsBlock,
		PromotedBuyBox,
		ProvideStreamingChartData,
		QuickActionBar,
		SeasonsList,
		SimilarTitlesGraphql,
		SimilarTitlesProvider,
		SpinningText,
		TitleBackdropsWrapper,
		TitleBackdrops,
		TitleBlock,
		TitleClips,
		TitleCredits,
		TitleInfo,
		TitleListProvider,
		TitleSynopsis,
		TitleDetailStreamingChart,
		LazyUpcomingContent,
		PopularTitlesProvider,
		LazyWhyToWatch,
		SponsoredRecommendation,
	},
	metaInfo() {
		return this.head;
	},
	apollo: {
		article: {
			query: GetTitleDetailArticleDocument,
			variables(): GetTitleDetailArticleQueryVariables {
				return {
					fullPath: this.$route.fullPath,
					language: this.language,
					country: this.country,
				};
			},
			update({ urlV2: data }: GetTitleDetailArticleQuery) {
				type Node = AssertByTypename<typeof data.node, 'Movie' | 'Show' | 'Season'>;
				// only ONE ArticleType.TitleDetailArticle is expected per detail page
				const [article] = (data.node as Node).content?.articles ?? [];
				return article ?? null;
			},
			errorPolicy: 'ignore',
			skip() {
				// exclude request if the page is opened through a modal (meaning the title doesn't have a fullpath)
				return !this.byFullPath && !this.$route.name?.startsWith('app.detail');
			},
		},
		snippets: {
			query: GetTitleDetailSnippetsDocument,
			variables(): GetTitleDetailSnippetsQueryVariables {
				return {
					fullPath: this.$route.fullPath,
					language: this.language,
					country: this.country,
				};
			},
			update({ urlV2: data }: GetTitleDetailSnippetsQuery) {
				type Node = AssertByTypename<typeof data.node, 'Movie' | 'Show' | 'Season'>;
				return (data.node as Node).content?.articles ?? [];
			},
			errorPolicy: 'ignore',
			skip() {
				// exclude request if the page is opened through a modal (meaning the title doesn't have a fullpath)
				return !this.byFullPath && !this.$route.name?.startsWith('app.detail');
			},
		},
		/**
		 * SponsoredRecommendation special case: SeoUser
		 * we need to send the request from the client in order to have a meaningful SR bidding/tracking
		 * Manually overwrite the titles list
		 */
		sponsoredAdData: {
			query: GetUrlTitleDetailsDocument,
			fetchPolicy: 'no-cache',
			prefetch: false,
			variables(): GetUrlTitleDetailsQueryVariables {
				return this.queryVariables;
			},
			update({ urlV2: data }: GetUrlTitleDetailsQuery) {
				const node = data.node as AssertByTypename<
					GetUrlTitleDetailsQuery['urlV2']['node'],
					'Movie' | 'Show' | 'Season'
				>;
				const similarTitles =
					node.__typename === 'Season' ? node?.show?.similarTitlesV2 : node?.similarTitlesV2;
				this.isSeoSRLoaded = true;
				return similarTitles?.sponsoredAd;
			},
			error(e) {
				console.log(e);
				process.client
					? toastError({ message: this.$t('WEBAPP_ALERT_ERROR_TITLE') as string, duration: 2000 })
					: null;
			},
			// we decided to always skip this request and pottially have List SRs in the cache.
			// if we wouldn't, the additional query could bring down other graphql requests.
			skip() {
				return (
					// exclude request if the page is opened through a modal (meaning the title doesn't have a fullpath)
					!this.byFullPath &&
					this.activeRoute?.meta?.tab !== 'detail' &&
					(!this.isSeoUser || this.isSeoSRLoaded)
				);
			},
		},
	},
	//APPLE_BACKDROP_EXP
	setup() {
		const { isArbitrating } = useArbitrage();

		return { isArbitrating };
	},
	//APPLE_BACKDROP_EXP
})
export default class TitleDetail extends Vue {
	TitleListType = TitleListType;

	activeClipId: string | null = null;
	episodeMaxLimit: number | null = 20;
	location = 'TitleDetail';
	article: TitleDetailArticleFragment | null = null;
	snippets: TitleDetailSnippetFragment[] = [];
	sponsoredAdData: SponsoredAdFragment = {} as SponsoredAdFragment;
	isSeoSRLoaded = false; // we only need this the first time, when it comes back from the SSR server

	// Used to show skeleton while buybox above the fold is loading to prevent CLS.
	// Could be used for similar cases with other components
	isReadyToRender = true;

	// for INP improvements:
	// we need an immediate change upon userinteraction.
	// the biggest user interaction on title detail pages (~40% user interactions) is navigating from title detail to another title detail, and it's the least performant, too.
	// this `perceivedLoading` will briefly (0ms) v-if in and out. so the children elements don't take up all of our performance budget.
	perceivedLoading = false;

	// APPLE_BACKDROP_EXP
	declare isArbitrating: boolean;
	// APPLE_BACKDROP_EXP

	@Prop() entityId: string | undefined;

	@Ref('content') readonly content!: Content;

	@InjectReactive(GenresByShortNameConstant) genresByShortName: Genres;

	@App.Getter isSeoUser: boolean;

	@Language.Getter country: string;
	@Language.Getter language: string;
	@Language.State webLocale: WebLocale;

	@TitleDetailsStore.Getter refferalModuleContext: SnowplowModuleContext | null;
	@TitleDetailsStore.Getter sponsoredAd: SponsoredAdFragment;
	@TitleDetailsStore.Action setSponsoredRecommendation: (sponsoredAd: SponsoredAdFragment | null) => void;
	@TitleDetailsStore.Action setTitleContext: (payload: SnowplowTitleContextGraphqlPayload) => void;
	@TitleDetailsStore.Action clearTitleContext: () => void;
	@TitleDetailsStore.Action setPermanentAudiences: (audiences: string[]) => void;
	@TitleDetailsStore.Action resetPermanentAudiences: () => void;

	@Routing.State activeRoute: Route;

	@User.Getter isLoggedIn: (providerName?: string) => boolean;
	@User.Getter isPremium: boolean;
	@User.State settings: UserSettings;

	@Experiment.Getter activeVariantsWithControlGroup: Record<string, string>;

	titleDetailsNodeQuery = undefined as
		| UseQueryReturn<GetNodeTitleDetailsQuery, GetNodeTitleDetailsQueryVariables>
		| undefined;
	titleDetailsFullPathQuery = undefined as
		| UseQueryReturn<GetUrlTitleDetailsQuery, GetUrlTitleDetailsQueryVariables>
		| undefined;

	created() {
		// useQuerys need to be called after the created hook, because vuex store values can't be accessed before.
		// @Watch decorator doesn't work on titleDetailNode with composables somehow, so we check `onResult` for changes.

		// by node id
		this.titleDetailsNodeQuery = useQuery<GetNodeTitleDetailsQuery, GetNodeTitleDetailsQueryVariables>(
			GetNodeTitleDetailsDocument,
			() => this.queryVariables as GetNodeTitleDetailsQueryVariables,
			() => ({ enabled: this.byNodeId && !!this.entityId })
		);
		this.titleDetailsNodeQuery.onResult(this.onTitleDetailNodeChange);
		// by fullpath
		this.titleDetailsFullPathQuery = useQuery<GetUrlTitleDetailsQuery, GetUrlTitleDetailsQueryVariables>(
			GetUrlTitleDetailsDocument,
			() => this.queryVariables as GetUrlTitleDetailsQueryVariables,
			() => ({ enabled: this.byFullPath && this.activeRoute?.meta?.tab === 'detail' })
		);
		this.titleDetailsFullPathQuery.onResult(this.onTitleDetailNodeChange);
	}

	get head() {
		if (!this.titleDetailNode) return {};

		return {
			meta: [
				{
					vmid: 'og:image',
					property: 'og:image',
					content: JW_CONFIG.IMAGESCALER_URL + this.titleDetailNode.content.fullPosterUrl,
				},
				{
					vmid: 'og:type',
					property: 'og:type',
					content: this.titleObjectType === ObjectType.Movie ? 'video.movie' : 'video.tv_show',
				},
				{ vmid: 'og:image:width', property: 'og:image:width', content: '718' },
				{ vmid: 'og:image:height', property: 'og:image:height', content: '1024' },

				// UPDATE_META_DESCRIPTIONS
				...(this.needsMetaDescriptionUpdate
					? [
							{
								vmid: 'description',
								name: 'description',
								content: this.metaDesciptionUpdate,
							},
							{
								vmid: 'og:description',
								property: 'og:description',
								content: this.metaDesciptionUpdate,
							},
					  ]
					: []),
				// UPDATE_META_DESCRIPTIONS
			],
		};
	}

	get byFullPath() {
		// byNodeId (entityId prop) takes precedence.
		return !this.byNodeId && !!(this.queryVariables as GetUrlTitleDetailsQueryVariables).fullPath;
	}

	get byNodeId() {
		return !!(this.queryVariables as GetNodeTitleDetailsQueryVariables).entityId;
	}

	get titleDetailsUrl() {
		if (this.byFullPath) {
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const { node, ...rest } =
				(this.titleDetailsFullPathQuery?.result.value?.urlV2 as AssertByTypename<
					GetUrlTitleDetailsQuery['urlV2'],
					'Url'
				>) || {};
			return rest;
		}
		// either byFullPath or byNodeId (no if else to not make ts trip up on any optional chaining)
		return this.titleDetailsNodeQuery?.result.value?.node as AssertByTypename<
			GetNodeTitleDetailsQuery['node'],
			'Url'
		>;
	}

	get titleDetailNode() {
		if (this.byFullPath) {
			const node = this.titleDetailsFullPathQuery?.result.value?.urlV2.node!;
			return node as AssertByTypename<typeof node, 'Movie' | 'Show' | 'Season'>;
		}
		// either byFullPath or byNodeId (no if else to not make ts trip up on any optional chaining)
		const node = this.titleDetailsNodeQuery?.result.value?.node!;
		return node as AssertByTypename<typeof node, 'Movie' | 'Show' | 'Season'>;
	}

	/* SPONSORED RECOMMENDATIONS */
	// one remaining issue:
	// when a SEO user gets a cached title detail page, that detail page does the additional SR request (sponsoredAdData).
	// when the user then continues clicking, they remain a SEO user, but new SR requests are sent via the titleDetailNode.
	//
	// we'd need a sustainable way to only use sponsoredAdData once for SEO users, and then continue with titleDetailNode.
	get _sponsoredAd(): SponsoredAdFragment | null {
		// take the additionally fetched sponsored ad.
		// because we made the request (bid) with it.
		if (this.isSeoUser) return this.sponsoredAdData;

		if (this.titleDetailNode.__typename === 'Movie' || this.titleDetailNode.__typename === 'Show')
			return this.titleDetailNode?.similarTitlesV2?.sponsoredAd ?? null;
		if (this.titleDetailNode.__typename === 'Season')
			return this.titleDetailNode?.show?.similarTitlesV2?.sponsoredAd ?? null;

		return null;
	}

	// Check to see if a SR campaign has an ID associated to it
	get hasSponsoredAd(): boolean {
		return !!this._sponsoredAd?.campaign?.node?.nodeId;
	}

	get isListSponsoredAd() {
		return this._sponsoredAd?.campaign?.node?.__typename === 'GenericTitleList';
	}
	/* SPONSORED RECOMMENDATIONS END */

	get notLoggedIn() {
		return !this.isLoggedIn();
	}

	get queryVariables(): GetUrlTitleDetailsQueryVariables | GetNodeTitleDetailsQueryVariables {
		// GraphQL query for TitleDetail doesn't support URL query params like `?serve_fallback=true`
		// so we have to remove them before making a request
		const fullPath = (this.activeRoute?.fullPath || '').split('?')[0];

		function decodePath(path: string) {
			return path
				.split(':')
				.map(part => decodeURI(part))
				.join(':');
		}

		return {
			fullPath: decodePath(fullPath),
			entityId: this.entityId, // 'tm1141995', // rebel moon
			language: this.language,
			country: this.country,
			episodeMaxLimit: this.episodeMaxLimit,
			platform: Platform.Web,
			allowSponsoredRecommendations: {
				appId: TrackingAppId,
				country: this.country,
				language: this.language,
				pageType: 'VIEW_TITLE_DETAIL',
				placement: SraPlacement.DetailPage,
				platform: Platform.Web,
				supportedObjectTypes,
				supportedFormats,
				testingMode: this.$route.query.hasOwnProperty('displaySR'),
			},
		};
	}

	get preventCLS() {
		return process.client && !this.isReadyToRender;
	}

	/**
	 * Inject lowPriorityCallback from App.vue
	 */
	@Inject({ default: () => {} }) disableScroll: any;
	@Inject({ default: () => {} }) enableScroll: any;

	@Watch('preventCLS', { immediate: true })
	onPreventCLS(value: boolean) {
		if (value) {
			disableScroll();
		} else {
			enableScroll();
		}
	}

	get hasTrailers() {
		return !!this.titleDetailNode.content.clips?.length;
	}

	get hasBundleOffers() {
		if (this.titleDetailNode.promotedBundles) {
			return this.titleDetailNode.promotedBundles?.length > 0;
		}

		return false;
	}

	get additionalContexts() {
		const refferedModuleContext: SnowplowModuleContext | null = this.refferalModuleContext;

		const contexts: SnowplowContext[] = [];
		if (refferedModuleContext) contexts.push(refferedModuleContext);

		return contexts;
	}

	get metaData() {
		if (!this.titleDetailsUrl) {
			return undefined;
		}
		return {
			...this.titleDetailsUrl,
			htmlContent: JSON.parse(this.titleDetailsUrl?.htmlContent || '{}'),
		};
	}

	get htmlContent() {
		return this.metaData?.htmlContent || {};
	}

	get title() {
		const isSeason = this.titleDetailNode.__typename === 'Season' && !!this.titleDetailNode.show;
		return isSeason ? this.titleDetailNode.show! : this.titleDetailNode;
	}

	get titleName() {
		const isSeason = this.titleDetailNode.__typename === 'Season' && !!this.titleDetailNode.show;
		return isSeason ? this.titleDetailNode.show?.content.title : this.titleDetailNode.content.title;
	}

	get titleId() {
		const isSeason = this.titleDetailNode.__typename === 'Season' && !!this.titleDetailNode.show;
		if (isSeason) {
			return this.titleDetailNode.show?.id;
		}
		return this.titleDetailNode.id;
	}

	get titleObjectId() {
		const isSeason = this.titleDetailNode.__typename === 'Season' && !!this.titleDetailNode.show;
		return isSeason ? this.titleDetailNode.show!.objectId : this.titleDetailNode.objectId;
	}

	get titleObjectType() {
		const isSeason = this.titleDetailNode.__typename === 'Season' && !!this.titleDetailNode.show;
		return isSeason ? this.titleDetailNode.show?.objectType : this.titleDetailNode.objectType;
	}

	get titlePosterUrl() {
		const isSeason = this.titleDetailNode.__typename === 'Season' && !!this.titleDetailNode.show;
		return isSeason ? this.titleDetailNode.show?.content.posterUrl : this.titleDetailNode.content.posterUrl;
	}

	get titleBackdrops() {
		const isSeason = this.titleDetailNode.__typename === 'Season' && !!this.titleDetailNode.show;
		return isSeason ? this.titleDetailNode.show?.content.backdrops : this.titleDetailNode.content.backdrops;
	}

	get seasonName() {
		const isSeason = this.titleDetailNode.__typename === 'Season' && !!this.titleDetailNode.show;
		return isSeason ? this.titleDetailNode.content.title : null;
	}

	get seasonNumber() {
		const isSeason = this.titleDetailNode.__typename === 'Season' && !!this.titleDetailNode.show;
		return isSeason ? this.titleDetailNode.content.seasonNumber : null;
	}

	get isStreamingChartCountry() {
		return streamingChartCountries.includes(this.country);
	}

	get displayStreamingChart() {
		return !this.isLoggedIn() && this.isStreamingChartCountry;
	}

	/* SAFARI RENDERING ISSUE FIX */
	//purposed added variable for the safari fix
	get isDesktopSafari() {
		if (process.server) {
			return false;
		}
		const ua = navigator.userAgent;
		const safariDesktop = /(Macintosh).*AppleWebKit.*Safari/;
		const excludedBrowsers = /Chrome|Firefox|CriOS|OPiOS|OPiOS|mercury|FxiOS/;
		return safariDesktop.test(ua) && !excludedBrowsers.test(ua);
	}

	handleBuyboxLoading(isLoading: boolean) {
		this.isReadyToRender = !isLoading;
	}

	get titleContent() {
		return this.titleDetailNode?.content;
	}

	get originalTitle() {
		const isMovie = this.titleDetailNode.__typename === 'Movie';
		const isShow = this.titleDetailNode.__typename === 'Show';
		return isMovie || isShow ? this.titleDetailNode.content.originalTitle : null;
	}

	get hasBackdropContent(): boolean {
		const hasCarouselImages = this.titleDetailNode.content.backdrops?.length > 0;
		const hasTrailerClips = this.backdropClips.length > 0;

		return hasCarouselImages || hasTrailerClips;
	}

	get backdropClips() {
		// @todo types
		return getBackdropClips(this.titleDetailNode as any);
	}

	// is the title detail page in loading mode for:
	// - requests
	// - async components loading (note: if we don't wait for async component loading, the CLS will increase)
	// - artifical perceived performance after a user interaction (for INP reasons)
	get isLoading() {
		return this.isDataLoading || !this.isReadyToRender || this.perceivedLoading;
	}

	// is the title detail page in loading mode for:
	// - requests
	get isDataLoading() {
		return this.titleDetailsFullPathQuery?.loading.value || this.titleDetailsNodeQuery?.loading.value;
	}

	get isPerceivedLoading() {
		return !this.titleDetailNode || this.perceivedLoading;
	}

	get isSeoTraffic() {
		return TrafficHelper.isSeoTraffic();
	}

	get showAdUnit(): boolean {
		return showFreemium() && !this.metaData?.htmlContent.exclude_from_ads;
	}

	get showCustomSynopsis() {
		return this.metaData?.htmlContent?.detail_heading != null && this.metaData?.htmlContent?.detail_content != null;
	}

	get showSynopsis(): boolean {
		return !this.showCustomSynopsis && !!this.titleDetailNode.content.shortDescription;
	}

	get seasonEpisodesAvailable() {
		return this.seasonEpisodes.length > 0;
	}

	get seasonEpisodes() {
		const isSeason = this.titleDetailNode.__typename === 'Season' && !!this.titleDetailNode.show;
		return isSeason ? this.titleDetailNode.episodes : [];
	}

	get seasonTotalEpisodeCount() {
		const isSeason = this.titleDetailNode.__typename === 'Season' && !!this.titleDetailNode.show;
		return isSeason ? this.titleDetailNode.totalEpisodeCount : null;
	}

	get isShow() {
		return this.titleDetailNode.__typename === 'Show';
	}

	get isSeason() {
		const isSeason = this.titleDetailNode.__typename === 'Season' && !!this.titleDetailNode.show;
		return isSeason;
	}

	get showSeasonsAvailable() {
		const isShow = this.titleDetailNode.__typename === 'Show';
		if (!isShow) return false;

		return (this.titleDetailNode.seasons ?? []).length > 0;
	}

	get recentEpisodes() {
		const isShow = this.titleDetailNode.__typename === 'Show';
		return isShow ? this.titleDetailNode.recentEpisodes : [];
	}

	get showRecentEpisodeList() {
		return (
			this.showSeasonsAvailable &&
			(!this.promoteSponsoredRecommendation || this.sponsoredRecommendationPromotion !== 'HIGH')
		);
	}

	get recentEpisodesAvailable() {
		const isShow = this.titleDetailNode.__typename === 'Show';
		return isShow && this.recentEpisodes ? this.recentEpisodes.length > 0 : false;
	}

	get credits() {
		const isSeason = this.titleDetailNode.__typename === 'Season' && !!this.titleDetailNode.show;
		const isMovie = this.titleDetailNode.__typename === 'Movie';
		const isShow = this.titleDetailNode.__typename === 'Show';
		if (isSeason) {
			return this.titleDetailNode.show.content.credits || [];
		}
		if (isMovie || isShow) {
			this.titleDetailNode.content.credits || [];
		}
		// should never happen™
		return [];
	}

	get titleFullPath() {
		const isSeason = this.titleDetailNode.__typename === 'Season' && !!this.titleDetailNode.show;
		return isSeason ? this.titleDetailNode.show?.content.fullPath : this.titleDetailNode.content.fullPath;
	}

	get isTitleUpcoming() {
		return !this.titleDetailNode.content.isReleased;
	}

	get showSimilarTitles() {
		return this.titleDetailNode && !this.metaData?.htmlContent.hide_similar_titles;
	}

	get curatedUpcomingHeadline() {
		switch (this.titleDetailNode.__typename) {
			case 'Movie':
				return this.$t('WEBAPP_UPCOMING_MOVIES_LIST');
			case 'Show':
			case 'Season':
				return this.$t('WEBAPP_UPCOMING_SHOWS_LIST');
		}
	}

	get firstTitleGenreShortName() {
		return this.titleDetailNode.content.genres?.[0]?.shortName ?? null;
	}

	get similarUpcomingHeadline() {
		if (!this.firstTitleGenreShortName) return this.$t('WEBAPP_UPCOMING_TITLES_LIST');

		const genre = this.genresByShortName[this.firstTitleGenreShortName]?.translation;
		switch (this.titleDetailNode.__typename) {
			case 'Movie':
				return this.$t('WEBAPP_UPCOMING_GENRE_MOVIES_LIST', { genre });
			case 'Show':
			case 'Season':
				return this.$t('WEBAPP_UPCOMING_GENRE_SHOWS_LIST', { genre });
		}
	}

	get similarUpcomingFilters(): TitleFilter {
		//  NewTitlesEdge returns a node of type 'MovieOrSeason,' so it only accepts 'ShowSeason' and 'Movie' object types for filtering.
		const objectType = this.titleObjectType === ObjectType.Show ? ObjectType.ShowSeason : this.titleObjectType;
		return {
			genres: [this.firstTitleGenreShortName].filter(f => f),
			objectTypes: [objectType],
		};
	}

	get customTitleListID() {
		if (JW_CONFIG.DOMAIN.includes('moviecycle')) {
			return 'tl-tu-7c7fce97-990c-4bdd-b3b1-2e3bf29a3fb6';
		}

		return this.titleObjectType === ObjectType.Movie
			? 'tl-tu-92861397-5f87-4233-ab53-cd06b7a97294'
			: 'tl-tu-166ee44c-3f7c-4f61-8650-13baedc7b4b8';
	}

	get customTitleListTitle(): PopularTitle {
		return {
			id: this.titleObjectId,
			object_type: this.titleObjectType.toLowerCase(),
			title: this.titleName,
			jw_entity_id: this.titleId,
		} as PopularTitle;
	}
	get quickActionBarProps(): QuickActionBarProps {
		const isSeason = this.titleDetailNode.__typename === 'Season' && !!this.titleDetailNode.show;
		const props: QuickActionBarProps = {
			titleId: this.titleDetailNode.id,
			titleName: this.titleDetailNode.content.title,
			titleObjectId: this.titleDetailNode.objectId,
			titleObjectType: this.titleDetailNode.objectType,
			titlePosterUrl: this.titleDetailNode.content.posterUrl ?? '',
			titleBackdrops: this.titleDetailNode.content.backdrops,
			titleContentType: this.titleContentType,
			titleSeenEpisodeCount: this.seenEpisodeCount,
			isInWatchlist: this.isTitleInWatchNext,
			isInSeenList: this.isTitleInCaughtUp,
			isInLikeList: this.isTitleInLikelist,
			isInDislikeList: this.isTitleInDislikelist,
			isInCustomList: this.isInCustomList,
			isTvShowTracked: this.isTvShowTracked,
			isTitleUpcoming: this.isTitleUpcoming,
			isTitleReleased: this.titleDetailNode.content.isReleased,
			dislikelistAdditions: this.interactions?.dislikelistAdditions ?? 0,
			jwVotes: this.interactions?.votesNumber ?? 0,
			likelistAdditions: this.interactions?.likelistAdditions ?? 0,
			seasonNumber: isSeason ? this.titleDetailNode.content.seasonNumber : undefined,
			without: [TitleListName.SEENLIST],
			fullWidth: true,
			additionalContexts: this.additionalContexts,
			influencedSponsoredAd: this._sponsoredAd || undefined,
		};

		if (isSeason && this.showDetails) {
			props.showDetails = this.showDetails;
		}

		return props;
	}

	// Required for quick actions, when we want to toggle status of the show on season page
	get showDetails() {
		const isSeason = this.titleDetailNode.__typename === 'Season' && !!this.titleDetailNode.show;
		if (isSeason) {
			return {
				id: this.titleDetailNode.show?.id,
				objectType: this.titleDetailNode.show?.objectType,
				objectId: this.titleDetailNode.show?.objectId,
				title: this.titleDetailNode.show?.content?.title,
				isReleased: this.titleDetailNode.show?.content?.isReleased,
			};
		}

		return null;
	}

	get interactions() {
		const isMovie = this.titleDetailNode.__typename === 'Movie';
		const isShow = this.titleDetailNode.__typename === 'Show';
		const isSeason = this.titleDetailNode.__typename === 'Season' && !!this.titleDetailNode.show;
		if (isSeason && this.titleDetailNode.show?.totalSeasonCount <= 1) {
			return null; // show nothing for seasons
		}
		if (isMovie || isShow) {
			return this.titleDetailNode.content.interactions;
		}
		// should never happen™
		return null;
	}

	get hidePromotedBuyBox() {
		return this.metaData?.htmlContent.hide_sponsored_buybox === true;
	}

	@Watch('isLoggedIn')
	onLoggedInChange() {
		this.titleDetailsFullPathQuery?.refetch();
		this.titleDetailsNodeQuery?.refetch();
	}

	// when leaving page through modal
	destroyed() {
		this.onLeaveTitleDetailPage();
	}

	// when leaving page through route
	@Watch('activeRoute')
	async onRouteChange(route: Route) {
		const titleHasChanged = (route.name || '').includes('app.detail') && this.titleContent?.fullPath !== route.path;

		if (titleHasChanged) {
			this.scrollToTop();
			// We need to reset title object, to prevent CLS issue;
			// @ts-ignore
			this.titleDetails = {};
			this.clearTitleContext();
			this.setEpisodeMaxLimit(20);

			// navigating from (popular or title page) -> title detail page is the most expensive userinteraction.
			this.perceivedLoading = true;
			this.$nextTick().then(() => (this.perceivedLoading = false));
		}

		if (!route.name?.includes('app.detail')) {
			this.onLeaveTitleDetailPage();
		}
	}

	private onLeaveTitleDetailPage() {
		this.resetPermanentAudiences();
		this.setSponsoredRecommendation(null);
	}

	private onTitleDetailNodeChange() {
		if (!this.titleDetailNode) {
			return;
		}
		// reset state
		this.clearTitleContext();
		this.setEpisodeMaxLimit(20);

		const isSeason = this.titleDetailNode.__typename === 'Season' && !!this.titleDetailNode.show;
		const payload: SnowplowTitleContextGraphqlPayload = [
			this.titleObjectId,
			this.titleObjectType,
			isSeason ? this.titleDetailNode.content.seasonNumber : undefined,
			undefined,
			this.titleContentType,
		];

		this.setTitleContext(payload);

		// set permanent audiences
		if (this.titleDetailNode.__typename !== 'Season') {
			this.setPermanentAudiences(this.titleDetailNode.permanentAudiences);
		}

		if (this.byFullPath) {
			// set meta
			this.$store.dispatch('meta/setTitleJsonLD', { title: this.titleDetailNode });

			// make sure the pageview tracking event is added to the pooling on client only
			if (process.client) {
				TrackingHelper.trackPage(this.$route.path, this.additionalContexts);
			}
		}
	}

	get isShowAvailabilityBadge() {
		return this.isLoggedIn() && !!this.availabilityBadgeDate;
	}

	get titleContentType() {
		if (this.isShowAvailabilityBadge) {
			return this.availabilityBadgeType === AvailabilityBadgeType.Upcoming
				? TitleContentType.ComingSoon
				: TitleContentType.LeavingSoon;
		}

		return undefined;
	}

	get seenEpisodeCount() {
		const isShow = this.titleDetailNode.__typename === 'Show';
		return isShow ? this.titleDetailNode.seenState.seenEpisodeCount : undefined;
	}

	get upcomingReleaseFromSelectedPackages() {
		const userPackages = this.settings.providers;

		return (
			this.titleDetailNode.content.upcomingReleases
				?.filter(item => diffInMilliseconds(new Date(item.releaseDate)) > 0)
				.filter(item => (item?.releaseCountDown ?? diffInDays(new Date(item.releaseDate))) < 30)
				.find(item => item.package?.shortName && userPackages.includes(item.package?.shortName)) || null
		);
	}

	get leavingOfferFromSelectedPackages() {
		const userPackages = this.settings.providers;
		return (
			this.titleDetailNode.availableTo
				?.filter(item => (item?.availableCountDown ?? diffInDays(new Date(item.availableToDate))) < 30)
				.find(item => userPackages.includes(item.package?.shortName)) || null
		);
	}

	get availabilityBadgeType() {
		return !!this.upcomingReleaseFromSelectedPackages
			? AvailabilityBadgeType.Upcoming
			: AvailabilityBadgeType.Leaving;
	}

	get availabilityBadgeDate() {
		if (!!this.upcomingReleaseFromSelectedPackages) {
			return this.upcomingReleaseFromSelectedPackages.releaseDate;
		}

		if (!!this.leavingOfferFromSelectedPackages) {
			return this.leavingOfferFromSelectedPackages.availableToDate;
		}

		return null;
	}

	get availabilityBadgeCountdown() {
		if (!!this.upcomingReleaseFromSelectedPackages) {
			return this.upcomingReleaseFromSelectedPackages.releaseCountDown;
		}

		if (!!this.leavingOfferFromSelectedPackages) {
			return this.leavingOfferFromSelectedPackages.availableCountDown;
		}

		return null;
	}

	get isInCustomList() {
		const isSeason = this.titleDetailNode.__typename === 'Season' && !!this.titleDetailNode.show;
		const isMovie = this.titleDetailNode.__typename === 'Movie';
		const isShow = this.titleDetailNode.__typename === 'Show';
		if (isSeason) {
			return !!this.titleDetailNode.show?.customlistEntries.length;
		}
		if (isMovie || isShow) {
			return !!this.titleDetailNode.customlistEntries.length;
		}
		// should never happen™
		return false;
	}

	get isTvShowTracked() {
		const isSeason = this.titleDetailNode.__typename === 'Season' && !!this.titleDetailNode.show;
		const isShow = this.titleDetailNode.__typename === 'Show';
		if (isSeason) {
			return !!this.titleDetailNode.show?.tvShowTrackingEntry?.createdAt;
		}

		return isShow ? !!this.titleDetailNode.tvShowTrackingEntry?.createdAt : false;
	}

	// START: sponsored recommendations
	get sponsoredRecommendationPromotion() {
		return 'NONE';
	}

	get isSponsoredRecommendationCohort() {
		const isMovie = this.titleDetailNode.__typename === 'Movie';
		const isShow = this.titleDetailNode.__typename === 'Show';
		return isMovie || isShow ? this.titleDetailNode.similarTitlesV2?.sponsoredAd?.holdoutGroup : false;
	}

	get promoteSponsoredRecommendation() {
		return (
			this.sponsoredAd &&
			this.isSponsoredRecommendationCohort &&
			!this.isPremium &&
			this.sponsoredRecommendationPromotion !== 'NONE'
		);
	}
	// END: sponsored recommendation

	get titleSeenState() {
		const isShow = this.titleDetailNode.__typename === 'Show';
		return isShow ? this.titleDetailNode.seenState : undefined;
	}

	get seasonSeenState() {
		const isSeason = this.titleDetailNode.__typename === 'Season' && !!this.titleDetailNode.show;
		return isSeason ? this.titleDetailNode.show.seenState : undefined;
	}

	get isTitleInWatchNext() {
		const isSeason = this.titleDetailNode.__typename === 'Season' && !!this.titleDetailNode.show;
		const isMovie = this.titleDetailNode.__typename === 'Movie';
		const isShow = this.titleDetailNode.__typename === 'Show';
		if (isSeason) {
			return !!this.titleDetailNode.show?.watchlistEntryV2?.createdAt;
		}
		if (isMovie || isShow) {
			return !!this.titleDetailNode.watchlistEntryV2?.createdAt;
		}
		// should never happen™
		return false;
	}

	get isTitleInLikelist() {
		const isSeason = this.titleDetailNode.__typename === 'Season' && !!this.titleDetailNode.show;
		const isMovie = this.titleDetailNode.__typename === 'Movie';
		const isShow = this.titleDetailNode.__typename === 'Show';
		if (isSeason) {
			return !!this.titleDetailNode.show?.likelistEntry?.createdAt;
		}
		if (isMovie || isShow) {
			return !!this.titleDetailNode.likelistEntry?.createdAt;
		}
		// should never happen™
		return false;
	}

	get isTitleInDislikelist() {
		const isSeason = this.titleDetailNode.__typename === 'Season' && !!this.titleDetailNode.show;
		const isMovie = this.titleDetailNode.__typename === 'Movie';
		const isShow = this.titleDetailNode.__typename === 'Show';
		if (isSeason) {
			return !!this.titleDetailNode.show?.dislikelistEntry?.createdAt;
		}
		if (isMovie || isShow) {
			return !!this.titleDetailNode.dislikelistEntry?.createdAt;
		}
		// should never happen™
		return false;
	}

	get isTitleInCaughtUp() {
		const isMovie = this.titleDetailNode.__typename === 'Movie';
		return isMovie
			? !!this.titleDetailNode.seenlistEntry?.createdAt
			: this.titleDetailNode.seenState?.progress === 100;
	}

	get objectTypeTranslation(): Partial<Record<ObjectType, string>> {
		return {
			[ObjectType.Movie]: this.$t('WEBAPP_MOVIES') as string,
			[ObjectType.Show]: this.$t('WEBAPP_TVSHOWS') as string,
		};
	}

	get showInterlinkingModules() {
		// only render the new interlinking module for logged-out users.
		return !this.isLoggedIn();
	}

	get freeSimilarUpcomingFilters(): TitleFilter {
		return {
			monetizationTypes: [MonetizationType.Free],
			objectTypes: [this.titleObjectType],
		};
	}

	get director() {
		const [director] = this.credits.filter(credit => credit.role === CreditRole.Director);
		return director;
	}

	get filtersByDirector(): TitleFilter | undefined {
		return this.director?.personId
			? {
					personId: 'tp' + this.director.personId,
					personRole: CreditRole.Director,
					objectTypes: [this.titleObjectType],
					monetizationTypes: [MonetizationType.Flatrate],
			  }
			: undefined;
	}

	get mainActor() {
		const [actor] = this.credits.filter(credit => credit.role === CreditRole.Actor) ?? [];
		return actor;
	}

	get filtersByActor(): TitleFilter | undefined {
		return this.mainActor?.personId
			? {
					personId: 'tp' + this.mainActor.personId,
					personRole: CreditRole.Actor,
					objectTypes: [this.titleObjectType],
					monetizationTypes: [MonetizationType.Flatrate],
			  }
			: undefined;
	}

	setEpisodeMaxLimit(value: number | null) {
		this.episodeMaxLimit = value;
	}

	scrollToTop() {
		this.content?.scrollToTop();
	}

	get titleHasDisneyOffer() {
		return [WebLocale.au, WebLocale.nz].includes(this.webLocale) && this.titleDetailNode.disneyOffersCount > 0;
	}

	mounted() {
		useArbitrage();
		this.isMounted = true;

		setTimeout(() => {
			const query = this.activeRoute.query;
			if (this.isAVODActive && query['package'] === 'plc') {
				ModalHelper.openModal(
					PlexPlayerModal,
					{ offer: this.titleDetailNode?.plexPlayerOffers[0], title: this.titleDetailNode },
					{ cssClass: 'trailer-modal' },
					'',
					true,
					[]
				);
			}
		});
	}

	get isAVODActive() {
		/**
		 * It appears that the ‘Plex Player’ Offer is only available in the US.
		 * For Other Countries When filtering by it in the GraphQL query, instead of receiving an empty array,
		 * we are getting all the offers. To fix it, I’ve added a filter here.
		 */
		return (this.titleDetailNode?.plexPlayerOffers ?? []).filter(el => el.package.shortName === 'pxp').length > 0;
	}

	// get showNewBuyBox() {
	// 	return !!(
	// 		['GB', 'US'].includes(this.country) &&
	// 		this.titleDetailNode?.objectType === ObjectType.Movie &&
	// 		!this.isLoggedIn()
	// 	);
	// }

	// APPLE_BACKDROP_EXP
	isMounted = false;
	// APPLE_BACKDROP_EXP

	// UPDATE_META_DESCRIPTIONS

	get lastUpdated() {
		return this.titleDetailNode.maxOfferUpdatedAt ? this.titleDetailNode.maxOfferUpdatedAt : '';
	}

	get timestampDateTime() {
		const { date, time } = timestamp(
			this.lastUpdated,
			{ year: 'numeric', month: 'long', day: 'numeric' },
			this.country,
			this.language
		);
		return { date, time };
	}

	get needsMetaDescriptionUpdate() {
		return (
			allowedCountryCodes.includes(this.country) &&
			this.language === 'en' &&
			titleMetaDescriptionsData.some(title => title.id === this.titleDetailNode.id)
		);
	}
	get metaDesciptionUpdate() {
		if (!this.needsMetaDescriptionUpdate) return this.metaData?.metaTitle;

		const newMetaData = titleMetaDescriptionsData.find(title => title.id === this.titleDetailNode.id);

		return newMetaData?.timestampRequired && !!this.lastUpdated
			? appendMetaDescription(
					newMetaData.descriptionNew,
					this.timestampDateTime.date,
					this.timestampDateTime.time
			  )
			: newMetaData?.descriptionNew;
	}
	// UPDATE_META_DESCRIPTIONS

	// APPLE_BACKDROP_EXP

	get isAppleBackdropExp() {
		return !!this.activeVariantsWithControlGroup[AppleBackdropExpName];
	}

	get appleBackdropExpVariant() {
		if (this.isAppleBackdropExp) return this.activeVariantsWithControlGroup[AppleBackdropExpName];
		return '';
	}

	hasAppleTv() {
		if (!this.isMounted) return false;
		return (
			this.titleDetailNode.appleOffers &&
			this.titleDetailNode.appleOffers.some(offer => offer.package.packageId === 2)
		);
	}

	hasAppleTvPlusOffer() {
		if (!this.isMounted) return false;
		return (
			this.titleDetailNode.appleOffers &&
			this.titleDetailNode.appleOffers.some(offer => offer.package.packageId === 350)
		);
	}

	get appleBackdropExpActive(): boolean {
		if (!this.isMounted || !this.isAppleBackdropExp || this.isLoggedIn()) return false;

		return validateAppleBackdropExperiment(this.appleBackdropExpVariant) && !this.isArbitrating;
	}

	get appleBackdropExpProps() {
		return {
			appleBackdropExpActive: this.appleBackdropExpActive,
			...(this.isAppleBackdropExp && { appleBackdropExpVariant: this.appleBackdropExpVariant }),
			hasAppleTv: this.hasAppleTv(),
			hasAppleTvPlusOffer: this.hasAppleTvPlusOffer(),
		};
	}

	// APPLE_BACKDROP_EXP
}
